import { useCallback, useEffect, useRef, useState } from 'react';
import { SupplierItem } from './useSuppliers';
import { useLazyPurchaseRequestEmailTemplateQuery } from 'api';

interface UseEmailContentProps {
  suppliers: SupplierItem[];
  generateItemsTable: () => string;
}

export const useEmailContent = ({ suppliers, generateItemsTable }: UseEmailContentProps) => {
  const [selectedLanguage, setSelectedLanguage] = useState<string>('');
  const selectedLanguageRef = useRef<string>('');
  const [languageEmailBody, setLanguageEmailBody] = useState<Record<string, any>>({});
  const [languageEmailSubject, setLanguageEmailSubject] = useState<Record<string, any>>({});
  const languageEmailBodyRef = useRef<Record<string, any>>({});
  const languageEmailSubjectRef = useRef<Record<string, any>>({});
  const [triggerFetchTemplate] = useLazyPurchaseRequestEmailTemplateQuery();

  useEffect(() => {
    selectedLanguageRef.current = selectedLanguage;
  }, [selectedLanguage]);

  useEffect(() => {
    languageEmailBodyRef.current = languageEmailBody;
  }, [languageEmailBody]);

  useEffect(() => {
    languageEmailSubjectRef.current = languageEmailSubject;
  }, [languageEmailSubject]);

  useEffect(() => {
    if (!selectedLanguage && suppliers.length) {
      setSelectedLanguage(suppliers[0].languageId);
    }
  }, [suppliers, selectedLanguage]);

  const handleLanguageChange = useCallback((value: string) => {
    setSelectedLanguage(value);
    selectedLanguageRef.current = value;
  }, []);

  const prepareEmailTemplate = useCallback(
    (templateContent: Record<string, string>) => {
      const itemsTable = generateItemsTable();
      // Check if the template already contains an items placeholder
      if (templateContent?.content?.includes('{{ITEMS}}')) {
        return templateContent.content.replace('{{ITEMS}}', itemsTable);
      } else {
        // If no placeholder, append the table to the end of the template
        return templateContent + '<p>Requested Items:</p>' + itemsTable;
      }
    },
    [generateItemsTable]
  );

  const fetchEmailTemplate = useCallback(
    async (languageId: string) => {
      const { data: emailTemplate } = await triggerFetchTemplate({ languageId });
      return emailTemplate;
    },
    [triggerFetchTemplate]
  );

  const handleBodyChange = useCallback((e: string) => {
    const cleaned = e.replace(/<[^>]*>/g, '').trim();
    const currentLang = selectedLanguageRef.current;
    if (currentLang) {
      if (!cleaned) {
        const updatedBody = { ...languageEmailBodyRef.current };
        delete updatedBody[currentLang];
        setLanguageEmailBody(updatedBody);
      } else {
        setLanguageEmailBody({
          ...languageEmailBodyRef.current,
          [currentLang]: e,
        });
      }
    }
  }, []);

  const handleSubjectChange = useCallback((e: string) => {
    const cleaned = e.replace(/<[^>]*>/g, '').trim();
    const currentLang = selectedLanguageRef.current;
    if (currentLang) {
      if (!cleaned) {
        const updatedSubject = { ...languageEmailSubjectRef.current };
        delete updatedSubject[currentLang];
        setLanguageEmailSubject(updatedSubject);
      } else {
        setLanguageEmailSubject({
          ...languageEmailSubjectRef.current,
          [currentLang]: e,
        });
      }
    }
  }, []);

  const handleSupplierAdded = useCallback(
    async (languageId: string) => {
      const emailTemplate = await fetchEmailTemplate(languageId);

      // Add items table to the email template
      const emailTemplateWithItems = prepareEmailTemplate(emailTemplate.data.content || '');

      setLanguageEmailBody((oState) => {
        if (!languageId || oState[languageId]) {
          return oState;
        } else {
          return {
            ...oState,
            [languageId]: emailTemplateWithItems,
          };
        }
      });

      setLanguageEmailSubject((oState) => {
        if (!languageId || oState[languageId]) {
          return oState;
        } else {
          return {
            ...oState,
            [languageId]: emailTemplate?.data?.subject,
          };
        }
      });
    },
    [fetchEmailTemplate, prepareEmailTemplate]
  );

  const handleSupplierRemoved = useCallback(
    (updatedSuppliers: SupplierItem[]) => {
      // Get remaining languageIds
      const remainingLanguageIds = updatedSuppliers.map((s) => s.languageId);

      const updatedLanguageEmailBody = Object.fromEntries(
        Object.entries(languageEmailBody).filter(([langId]) => remainingLanguageIds.includes(langId))
      );
      const updatedLanguageEmailSubject = Object.fromEntries(
        Object.entries(languageEmailSubject).filter(([langId]) => remainingLanguageIds.includes(langId))
      );

      if (selectedLanguage && !updatedLanguageEmailBody[selectedLanguage]) {
        setSelectedLanguage('');
      }

      setLanguageEmailBody(updatedLanguageEmailBody);
      setLanguageEmailSubject(updatedLanguageEmailSubject);
    },
    [languageEmailBody, languageEmailSubject, selectedLanguage]
  );

  const uniqueLanguages = useCallback(() => {
    return Array.from(new Map(suppliers.map((i) => [i.languageId, i])).values());
  }, [suppliers]);

  return {
    selectedLanguage,
    languageEmailBody,
    languageEmailSubject,
    handleLanguageChange,
    handleBodyChange,
    handleSubjectChange,
    handleSupplierAdded,
    handleSupplierRemoved,
    uniqueLanguages,
  };
};
